import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Package, Building2, Car, Wrench, Box, MapPin, ArrowRight, Phone, Mail, Clock, Shield, Zap, Globe2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import PortalSwitcher from "@/components/PortalSwitcher";
import TrackBookingsNudge from "@/components/TrackBookingsNudge";
import AppBadges from "@/components/AppBadges";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const itemTypeIcons = {
  parcel: Package,
  pallet: Box,
  furniture: Building2,
  container: Truck,
  vehicle: Car,
  machinery: Wrench,
};

export default function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [itemTypes, setItemTypes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [quote, setQuote] = useState(null);
  
  const [formData, setFormData] = useState({
    pickup_postcode: "",
    delivery_postcode: "",
    item_type: "",
    size: "",
    weight_kg: "",
    description: "",
  });

  useEffect(() => {
    fetchItemTypes();
  }, []);

  const fetchItemTypes = async () => {
    try {
      const response = await axios.get(`${API}/item-types`);
      setItemTypes(response.data.item_types);
      setSizes(response.data.sizes);
    } catch (error) {
      console.error("Error fetching item types:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setQuote(null);
  };

  const handleGetQuote = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_postcode || !formData.delivery_postcode || !formData.item_type || !formData.size) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/quote`, {
        ...formData,
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
      });
      setQuote(response.data);
      toast.success("Quote calculated successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to calculate quote";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToBooking = () => {
    if (quote) {
      navigate(`/booking/${quote.id}`);
    }
  };

  return (
    <div className="min-h-screen">
      <PortalSwitcher />
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Transport Anything 247
              </span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#calculator" className="nav-link">Get Quote</a>
              <a href="#services" className="nav-link">Services</a>
              <a href="#contact" className="nav-link">Contact</a>
              <a href="/blog" className="nav-link">Blog</a>
            </div>
            <a href="tel:+447757133163" className="hidden sm:flex items-center gap-2 text-orange-500 font-medium">
              <Phone className="h-4 w-4" />
              <span>+44 7757 133163</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-16">
        <div 
          className="hero-bg"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1551471698-c7787ff6b7ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwdHJ1Y2slMjBoaWdod2F5JTIwdWt8ZW58MHx8fHwxNzY4OTExNTg1fDA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-white animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Transport <span className="text-orange-500">Anything</span>,
                <br />Anywhere in the UK
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg">
                From parcels to machinery, get fast, <span className="text-orange-500 font-semibold">dedicated transport</span> across the UK. Instant quote. Live tracking. Same-day available.
              </p>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-orange-500" />
                  <span className="text-sm">Fully Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <span className="text-sm">24/7 Service</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-orange-500" />
                  <span className="text-sm">Same Day Available</span>
                </div>
              </div>
            </div>

            {/* Quote Calculator */}
            <div id="calculator" className="animate-fade-in stagger-2">
              <TrackBookingsNudge />
              <Card className="calculator-card">
                <CardHeader className="pb-4">
                  <CardTitle className="calculator-header text-2xl">Get Instant Quote</CardTitle>
                  <p className="text-slate-500 text-sm">Fill in the details below for your free quote</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGetQuote} className="space-y-5">
                    {/* Postcodes */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pickup" className="form-label">Pickup Postcode *</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="pickup"
                            data-testid="pickup-postcode-input"
                            placeholder="e.g. SW1A 1AA"
                            value={formData.pickup_postcode}
                            onChange={(e) => handleInputChange("pickup_postcode", e.target.value.toUpperCase())}
                            className="pl-10 h-12"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="delivery" className="form-label">Delivery Postcode *</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="delivery"
                            data-testid="delivery-postcode-input"
                            placeholder="e.g. M1 1AA"
                            value={formData.delivery_postcode}
                            onChange={(e) => handleInputChange("delivery_postcode", e.target.value.toUpperCase())}
                            className="pl-10 h-12"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Item Type */}
                    <div>
                      <Label className="form-label">Item Type *</Label>
                      <Select
                        value={formData.item_type}
                        onValueChange={(value) => handleInputChange("item_type", value)}
                      >
                        <SelectTrigger data-testid="item-type-select" className="h-12">
                          <SelectValue placeholder="Select item type" />
                        </SelectTrigger>
                        <SelectContent>
                          {itemTypes.map((item) => {
                            const Icon = itemTypeIcons[item.id] || Package;
                            return (
                              <SelectItem key={item.id} value={item.id}>
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  <span>{item.name}</span>
                                  <span className="text-slate-400 text-xs ml-auto">{item.surcharge}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Size */}
                    <div>
                      <Label className="form-label">Size *</Label>
                      <Select
                        value={formData.size}
                        onValueChange={(value) => handleInputChange("size", value)}
                      >
                        <SelectTrigger data-testid="size-select" className="h-12">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizes.map((size) => (
                            <SelectItem key={size.id} value={size.id}>
                              <div className="flex items-center gap-2">
                                <span>{size.name}</span>
                                <span className="text-slate-400 text-xs">({size.multiplier})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Weight & Description */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="weight" className="form-label">Weight (kg)</Label>
                        <Input
                          id="weight"
                          data-testid="weight-input"
                          type="number"
                          placeholder="Optional"
                          value={formData.weight_kg}
                          onChange={(e) => handleInputChange("weight_kg", e.target.value)}
                          className="h-12"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description" className="form-label">Description</Label>
                        <Input
                          id="description"
                          data-testid="description-input"
                          placeholder="Optional"
                          value={formData.description}
                          onChange={(e) => handleInputChange("description", e.target.value)}
                          className="h-12"
                        />
                      </div>
                    </div>

                    {/* Get Quote Button */}
                    <Button
                      type="submit"
                      data-testid="get-quote-btn"
                      disabled={loading}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-medium text-lg"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="spinner" />
                          <span>Calculating...</span>
                        </div>
                      ) : (
                        <span>Get Instant Quote</span>
                      )}
                    </Button>
                  </form>

                  {/* Quote Result */}
                  {quote && (
                    <div data-testid="quote-result" className="mt-6 animate-fade-in">
                      <div className="price-display">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-slate-400 text-sm">Estimated Total</p>
                            <p data-testid="quote-total" className="price-value">
                              £{quote.total_cost.toFixed(2)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-400 text-sm">Distance</p>
                            <p data-testid="quote-distance" className="text-white font-mono text-xl font-bold">
                              {quote.distance_miles} miles
                            </p>
                          </div>
                        </div>

                        {/* Price Breakdown */}
                        <div className="border-t border-slate-700 pt-4 space-y-2">
                          {Object.entries(quote.breakdown).map(([label, value]) => (
                            value > 0 && (
                              <div key={label} className="flex justify-between text-sm">
                                <span className="text-slate-400">{label}</span>
                                <span className="text-white font-mono">£{value.toFixed(2)}</span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>

                      <Button
                        data-testid="proceed-booking-btn"
                        onClick={handleProceedToBooking}
                        className="w-full h-12 mt-4 bg-orange-500 hover:bg-orange-600 text-white font-medium"
                      >
                        Proceed to Booking
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us — trust + differentiation block (lightweight, conversion-focused) */}
      <section id="why-choose" className="py-16 md:py-20 bg-slate-50 border-t border-slate-100" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-xs font-semibold tracking-wider uppercase text-orange-500 mb-3" data-testid="why-choose-eyebrow">
              Why Transport Anything 247
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }} data-testid="why-choose-heading">
              Built for trust, priced transparently.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg">
              Six reasons UK customers and businesses book dedicated transport with us instead of a network courier.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: Zap, title: "Instant online quotes", desc: "Transparent pricing in seconds. No callbacks, no broker chains.", testid: "why-instant-quotes" },
              { icon: Truck, title: "Dedicated transport", desc: "Your load rides in one vehicle from pickup to delivery — no hubs.", testid: "why-dedicated" },
              { icon: MapPin, title: "Live tracking", desc: "See your driver's live ETA on a map from collection to drop-off.", testid: "why-live-tracking" },
              { icon: Shield, title: "Fully insured deliveries", desc: "Goods-in-transit cover included on every booking, no extras.", testid: "why-insured" },
              { icon: Clock, title: "Same-day available", desc: "Same-day collections across every major UK metro, 24/7.", testid: "why-same-day" },
              { icon: Globe2, title: "Nationwide network", desc: "Vetted drivers covering every UK postcode — Cornwall to Caithness.", testid: "why-nationwide" },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex gap-4 bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-300 hover:shadow-sm transition-all duration-200"
                data-testid={feature.testid}
              >
                <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-orange-50 flex items-center justify-center">
                  <feature.icon className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-base mb-1 inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" aria-hidden="true" />
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href="#calculator"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition shadow-sm hover:shadow"
              data-testid="why-choose-cta"
            >
              Get an Instant Quote <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              What We Transport
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              From small parcels to heavy machinery, we have the fleet and expertise to move anything safely and efficiently.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Package, title: "Parcels & Packages", desc: "Small to medium packages delivered safely", surcharge: "No surcharge" },
              { icon: Box, title: "Pallets", desc: "Industrial pallet shipping solutions", surcharge: "+10% surcharge" },
              { icon: Building2, title: "Furniture", desc: "Home and office furniture removal", surcharge: "+15% surcharge" },
              { icon: Truck, title: "Shipping Containers", desc: "Container transport and haulage", surcharge: "+25% surcharge" },
              { icon: Car, title: "Vehicles", desc: "Car and motorcycle transportation", surcharge: "+35% surcharge" },
              { icon: Wrench, title: "Construction Machinery", desc: "Heavy plant and equipment moving", surcharge: "+50% surcharge" },
            ].map((service, index) => (
              <Card key={index} className="feature-card group">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors duration-300">
                    <service.icon className="h-6 w-6 text-orange-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-500 text-sm mb-3">{service.desc}</p>
                  <span className="text-xs font-medium text-orange-500">{service.surcharge}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              How It Works
            </h2>
            <p className="text-slate-500">Simple, fast, and transparent pricing</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Get an instant quote", desc: "Transparent pricing in seconds — enter your postcodes and item details." },
              { step: "2", title: "Book your transport", desc: "Confirm your collection window and pay securely. Booking takes under 60 seconds." },
              { step: "3", title: "Track delivery live", desc: "Watch your driver's live ETA on a map from collection right through to delivery." },
            ].map((item, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 rounded-full bg-orange-500 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Get in Touch
              </h2>
              <p className="text-slate-500 mb-8">
                Have questions? Our team is here to help you 24/7. Contact us through any of the channels below.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">+44 7757 133163</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">customers@trasportanything247.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Hours</p>
                    <p className="font-medium text-slate-900">24/7 - Always Available</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1724222907702-d884d9c20507?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwdHJ1Y2slMjBoaWdod2F5JTIwdWt8ZW58MHx8fHwxNzY4OTExNTg1fDA&ixlib=rb-4.1.0&q=85"
                alt="Transport truck"
                className="w-full h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* App Store / Google Play badges — hidden until env-var URLs are set. */}
      {/* See /app/frontend/src/components/AppBadges.jsx for how to enable. */}
      <AppBadges variant="section" />

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  Transport Anything 247
                </span>
              </div>
              <p className="text-slate-400 text-sm">
                Professional transport services across the UK. From parcels to heavy machinery, we move it all.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a href="#calculator" className="footer-link block">Get Quote</a>
                <a href="#services" className="footer-link block">Services</a>
                <a href="#contact" className="footer-link block">Contact</a>
                <a href="/blog" className="footer-link block">Blog</a>
                <a href="/privacy" className="footer-link block" data-testid="footer-privacy-link">Privacy Policy</a>
                <a href="/terms" className="footer-link block" data-testid="footer-terms-link">Terms of Service</a>
                <a href="/cookies" className="footer-link block" data-testid="footer-cookies-link">Cookie Policy</a>
                <a href="/aup" className="footer-link block" data-testid="footer-aup-link">Acceptable Use</a>
                <a href="/support" className="footer-link block" data-testid="footer-support-link">Support</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Transparent Pricing</h4>
              <p className="text-slate-400 text-sm">Instant quotes based on distance, vehicle and transport requirements.</p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Transport Anything 247. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
